import React from "react";
import html2canvas from "html2canvas";
import { ToggleButton, ToggleButtonGroup, useTheme, IconButton, Box } from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  unstable_useBarSeries,
  unstable_useLineSeries,
  useXAxis,
  useXScale,
  useYScale,
} from "@mui/x-charts/hooks";

export const formatFullNumber = (value) =>
  new Intl.NumberFormat("en-IN").format(value ?? 0);

export const formatCompactNumber = (value) => {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue)) return "0";
  if (Math.abs(numericValue) < 1000) return formatFullNumber(numericValue);

  const compactValue = numericValue / 1000;
  return `${new Intl.NumberFormat("en-US", {
    maximumFractionDigits: compactValue >= 100 ? 0 : 1,
  }).format(compactValue)}k`;
};

export const ChartTypeToggle = ({ value, onChange }) => (
  <ToggleButtonGroup size="small" exclusive value={value} onChange={onChange}>
    <ToggleButton value="bar" aria-label="Bar chart" title="Bar chart">
      <BarChartIcon fontSize="small" />
    </ToggleButton>
    <ToggleButton value="line" aria-label="Line chart" title="Line chart">
      <ShowChartIcon fontSize="small" />
    </ToggleButton>
  </ToggleButtonGroup>
);

export const barValueLabel = (item) => {
  if (item.value == null) return null;
  return formatFullNumber(item.value);
};

export const TopBarLabel = ({ seriesId, dataIndex, children }) => {
  const theme = useTheme();
  const xScale = useXScale();
  const yScale = useYScale();
  const xAxis = useXAxis();
  const barSeries = unstable_useBarSeries();

  const series = barSeries?.series ?? {};
  const order = barSeries?.seriesOrder ?? [];
  const xValue = xAxis?.data?.[dataIndex];
  const value = series[seriesId]?.data?.[dataIndex];

  if (xValue == null || value == null) return null;

  const stackKeys = [];
  order.forEach((sid) => {
    const key = series[sid]?.stack ?? sid;
    if (!stackKeys.includes(key)) stackKeys.push(key);
  });
  const groupIndex = Math.max(
    stackKeys.indexOf(series[seriesId]?.stack ?? seriesId),
    0
  );

  const bandWidth = xScale.bandwidth();
  const gapRatio = xAxis?.barGapRatio ?? 0.1;
  const barWidth =
    bandWidth / (stackKeys.length + (stackKeys.length - 1) * gapRatio);
  const gap = gapRatio * barWidth;

  const x =
    (xScale(xValue) ?? 0) +
    groupIndex * (barWidth + gap) +
    barWidth / 2;
  const y = yScale(value) - 6;

  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="central"
      style={{
        fontSize: 10,
        fontWeight: 600,
        fill: (theme.vars || theme).palette.text.primary,
        pointerEvents: "none",
      }}
    >
      {children}
    </text>
  );
};

export const ChartValueMarkElement = (props) => {
  const { x, y, id, color, dataIndex, ...other } = props;
  const theme = useTheme();
  const lineSeries = unstable_useLineSeries();
  const seriesData = lineSeries?.series?.[id]?.data;
  const value = seriesData?.[dataIndex];
  const markerX = id === "createdShipments" ? x - 5 : x + 5;
  const labelY = y - (id === "createdShipments" ? 18 : 12);

  return (
    <g {...other} fill={color}>
      {value !== null && value !== undefined && (
        <text
          x={markerX}
          y={labelY}
          textAnchor="middle"
          dominantBaseline="central"
            style={{
              fill: (theme.vars || theme).palette.text.primary,
              fontSize: 10,
              fontWeight: 600,
              paintOrder: "stroke",
              stroke: (theme.vars || theme).palette.background.paper,
              strokeWidth: 4,
              strokeLinecap: "round",
              strokeLinejoin: "round",
            }}
        >
          {formatFullNumber(value)}
        </text>
      )}
      <circle cx={markerX} cy={y} r={4} />
    </g>
  );
};

export const ZoomableBox = ({
  enabled = true,
  scrollable = false,
  children,
  sx,
}) => {
  const containerRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const transform = React.useRef({ s: 1, x: 0, y: 0 });
  const pointers = React.useRef(new Map());
  const pinch = React.useRef(null);
  const pan = React.useRef(null);
  const [zoomed, setZoomed] = React.useState(false);

  const applyTransform = () => {
    const { s, x, y } = transform.current;
    if (contentRef.current)
      contentRef.current.style.transform = `translate(${x}px, ${y}px) scale(${s})`;
    setZoomed(s > 1.01);
  };

  const clampOffset = (x, y, s) => {
    const el = containerRef.current;
    if (!el || s <= 1) return { x: 0, y: 0 };
    const maxX = (el.clientWidth * (s - 1)) / 2;
    const maxY = (el.clientHeight * (s - 1)) / 2;
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  };

  const zoomAt = (clientX, clientY, factor) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ax = clientX - rect.left - rect.width / 2;
    const ay = clientY - rect.top - rect.height / 2;
    const { s, x, y } = transform.current;
    const ns = Math.min(6, Math.max(1, s * factor));
    if (ns === s) return;
    const off = clampOffset(x + (s - ns) * ax, y + (s - ns) * ay, ns);
    transform.current = { s: ns, ...off };
    applyTransform();
  };

  const zoomFromCenter = (factor) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, factor);
  };

  const resetZoom = () => {
    transform.current = { s: 1, x: 0, y: 0 };
    applyTransform();
  };

  const onPointerDown = (e) => {
    if (!enabled) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [p1, p2] = [...pointers.current.values()];
      pinch.current = {
        dist: Math.hypot(p1.x - p2.x, p1.y - p2.y),
        mid: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 },
      };
      pan.current = null;
    } else if (pointers.current.size === 1 && transform.current.s > 1) {
      pan.current = { x: e.clientX, y: e.clientY, offset: { ...transform.current } };
    }
  };

  const onPointerMove = (e) => {
    if (!enabled || !pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size >= 2 && pinch.current) {
      const [p1, p2] = [...pointers.current.values()];
      const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
      zoomAt(mid.x, mid.y, dist / (pinch.current.dist || dist));
      const dx = mid.x - pinch.current.mid.x;
      const dy = mid.y - pinch.current.mid.y;
      const { s } = transform.current;
      const off = clampOffset(transform.current.x + dx, transform.current.y + dy, s);
      transform.current = { s, ...off };
      applyTransform();
      pinch.current = { dist, mid };
    } else if (pointers.current.size === 1 && pan.current) {
      const dx = e.clientX - pan.current.x;
      const dy = e.clientY - pan.current.y;
      const off = clampOffset(
        pan.current.offset.x + dx,
        pan.current.offset.y + dy,
        transform.current.s
      );
      transform.current = { s: transform.current.s, ...off };
      applyTransform();
    }
  };

  const endPointer = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) {
      pan.current = null;
    } else if (pointers.current.size === 1 && transform.current.s > 1) {
      const [p] = [...pointers.current.values()];
      pan.current = { x: p.x, y: p.y, offset: { ...transform.current } };
    }
  };

  const buttonSx = {
    bgcolor: "background.paper",
    boxShadow: 2,
    border: "1px solid",
    borderColor: "divider",
    "&:hover": { bgcolor: "action.hover" },
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        overflow: "hidden",
        touchAction: enabled ? (scrollable ? "pan-y" : "none") : "auto",
        ...sx,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onDoubleClick={resetZoom}
    >
      <Box
        ref={contentRef}
        sx={{
          width: "100%",
          height: "100%",
          transformOrigin: "center center",
          willChange: "transform",
        }}
      >
        {children}
      </Box>

      {enabled && (
        <Box
          sx={{
            position: "absolute",
            bottom: 12,
            right: 12,
            display: "flex",
            gap: 0.5,
            zIndex: 10,
          }}
        >
          <IconButton size="small" sx={buttonSx} onClick={() => zoomFromCenter(1.3)}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={buttonSx} onClick={() => zoomFromCenter(1 / 1.3)}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          {zoomed && (
            <IconButton size="small" sx={buttonSx} onClick={resetZoom}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export const downloadChartSvg = async (containerEl, format, filename) => {
  if (!containerEl) return;

  const downloadCanvas = (canvas) => {
    const dataUrl = canvas.toDataURL(
      `image/${format === "jpeg" ? "jpeg" : "png"}`,
      0.95
    );
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `${filename}.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const exportWithHtml2Canvas = async () => {
    const canvas = await html2canvas(containerEl, {
      backgroundColor: getComputedStyle(containerEl).backgroundColor,
      scale: 2,
      useCORS: true,
      logging: false,
    });

    if (format === "pdf") {
      const { default: jsPDF } = await import("jspdf");
      const pdf = new jsPDF("landscape", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      pdf.addImage(
        canvas.toDataURL("image/jpeg", 0.95),
        "JPEG",
        margin,
        margin,
        pdfWidth - 2 * margin,
        pdfHeight - 2 * margin,
        undefined,
        "FAST"
      );
      pdf.save(`${filename}.pdf`);
    } else {
      downloadCanvas(canvas);
    }
  };

  const svg =
    containerEl.querySelector("svg.MuiChartsSurface-root") ||
    containerEl.querySelector("svg[class*='MuiChartsSurface']");
  // MUI can omit the generated chart class in some production builds. The
  // DOM snapshot is a reliable fallback in that case.
  if (!svg) {
    await exportWithHtml2Canvas();
    return;
  }

  const rect = svg.getBoundingClientRect();
  const width = Math.ceil(rect.width) || 1800;
  const height = Math.ceil(rect.height) || 800;

  const cloned = svg.cloneNode(true);
  cloned.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  cloned.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  cloned.setAttribute("width", width);
  cloned.setAttribute("height", height);

  const PRESENTATION_PROPS = [
    "fill",
    "stroke",
    "strokeWidth",
    "strokeDasharray",
    "strokeLinecap",
    "strokeLinejoin",
    "opacity",
    "fillOpacity",
    "strokeOpacity",
    "fontFamily",
    "fontSize",
    "fontWeight",
    "textAnchor",
    "dominantBaseline",
  ];

  const ATTR_NAME = {
    strokeWidth: "stroke-width",
    strokeDasharray: "stroke-dasharray",
    strokeLinecap: "stroke-linecap",
    strokeLinejoin: "stroke-linejoin",
    fillOpacity: "fill-opacity",
    strokeOpacity: "stroke-opacity",
    fontFamily: "font-family",
    fontSize: "font-size",
    fontWeight: "font-weight",
    textAnchor: "text-anchor",
    dominantBaseline: "dominant-baseline",
  };

  const CSS_DEFAULTS = {
    fill: "rgb(0, 0, 0)",
    stroke: "none",
    strokeWidth: "1px",
    strokeDasharray: "none",
    strokeLinecap: "butt",
    strokeLinejoin: "miter",
    opacity: "1",
    fillOpacity: "1",
    strokeOpacity: "1",
    fontWeight: "400",
    textAnchor: "start",
  };

  const liveEls = [svg, ...svg.querySelectorAll("*")];
  const cloneEls = [cloned, ...cloned.querySelectorAll("*")];

  liveEls.forEach((liveEl, i) => {
    const cloneEl = cloneEls[i];
    if (!cloneEl) return;
    const cs = getComputedStyle(liveEl);
    PRESENTATION_PROPS.forEach((prop) => {
      let value = cs[prop];
      if (prop === "fontFamily") {
        value = "Arial, Helvetica, sans-serif";
      }
      if (value == null || value === "" || value === CSS_DEFAULTS[prop]) return;
      const attr = ATTR_NAME[prop] ?? prop;
      cloneEl.setAttribute(attr, value);
    });
  });

  cloned.querySelectorAll("text, tspan").forEach((el) => {
    el.setAttribute("fill", "#111");
    if (el.style) {
      el.style.fill = "#111";
      el.style.stroke = "none";
    }
  });
  cloned
    .querySelectorAll(".MuiChartsAxis-line, .MuiChartsAxis-tick")
    .forEach((el) => {
      if (el.tagName.toLowerCase() !== "text") {
        el.setAttribute("stroke", "#999");
        if (el.style) el.style.stroke = "#999";
      }
    });

  const xml = new XMLSerializer().serializeToString(cloned);
  const svg64 =
    "data:image/svg+xml;base64," + window.btoa(unescape(encodeURIComponent(xml)));

  try {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = svg64;
    });

    const scale = 2;
    const canvas = document.createElement("canvas");
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    if (format === "pdf") {
      const { default: jsPDF } = await import("jspdf");
      const pdf = new jsPDF("landscape", "pt", "a4");
      const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      pdf.addImage(dataUrl, "JPEG", margin, margin, pdfWidth - 2 * margin, pdfHeight - 2 * margin, undefined, "FAST");
      pdf.save(`${filename}.pdf`);
    } else {
      downloadCanvas(canvas);
    }
  } catch (error) {
    // Production deployments can reject the serialized SVG (for example due
    // to a browser/CSP difference). Export the rendered chart DOM instead.
    console.warn("SVG chart export failed; using DOM export fallback", error);
    await exportWithHtml2Canvas();
  }
};
